/**
 * What wants a reviewer's attention on a review form, and the ordering every
 * affordance built on it agrees about.
 *
 * Deliberately neutral about *how* a form decides something wants attention.
 * Enumerating items is the form's own job — the extraction grid walks a schema,
 * the manifest ingest form walks its own sections — and the two have nothing in
 * common but the answer's shape. What they must not disagree about is the order
 * a reviewer meets those items in, how many are left, and where "next" goes, so
 * that is what lives here.
 *
 * Kinds are the caller's own string union rather than a fixed set: "the
 * extractor was unsure" and "e-Manifest says something else" are different
 * reasons and belong to different forms. The grouping order a reader sees is a
 * presentation choice, so it travels with the list control, not with the items.
 */

/** One thing the reviewer has to look at, and where it is. */
export interface AttentionItem<Kind extends string = string> {
  kind: Kind;
  /**
   * The form path to travel to, or `""` for something with nowhere to land — an
   * issue keyed to a field this form does not render. Such an item still counts
   * and still appears in the list; it just cannot be travelled to.
   *
   * A repeated row addresses the row rather than one of its cells, because the
   * question is about the row's existence and no single cell of it is the thing
   * to answer.
   */
  path: string;
  /** What the reviewer sees it called, with its row when it has one. */
  label: string;
  /** One line of why, for a list row. */
  detail?: string;
  /**
   * The key that answers this item, when it can be answered by keeping what the
   * field holds.
   *
   * Distinct from `path`, and deliberately: a repeated row's cell is answered
   * under a key tied to the row the extractor read, so the answer survives a row
   * above it being removed, while `path` is the cell's current position. The two
   * agree for a plain field and must not be conflated for anything else.
   *
   * Absent for an item that has no such answer — a validation error is not the
   * reviewer's to wave away.
   */
  answerKey?: string;
}

/**
 * How many items of each kind, for a banner's own sentences.
 *
 * Takes the kinds rather than deriving them from the items, so a kind with
 * nothing in it reads as zero instead of being absent — a caller rendering
 * "3 unsure, 0 conflicts" should not have to guard every lookup.
 */
export function countAttention<Kind extends string>(
  items: readonly AttentionItem<Kind>[],
  kinds: readonly Kind[]
): Record<Kind, number> {
  const counts = Object.fromEntries(kinds.map((kind) => [kind, 0])) as Record<Kind, number>;
  for (const item of items) counts[item.kind] += 1;
  return counts;
}

/**
 * Every control worth travelling to, in reading order, once each.
 *
 * De-duplicated by path because a control flagged for two reasons — an unsure
 * reading the record also disagrees with — is one place to go and one thing to
 * settle. Counting the reasons would promise a number that pressing "next" could
 * never work down.
 */
export function travellablePaths(items: readonly AttentionItem[]): string[] {
  const paths: string[] = [];
  for (const item of items) {
    if (item.path !== "" && !paths.includes(item.path)) paths.push(item.path);
  }
  return paths;
}

/**
 * The next path after `from`, wrapping around, or null when there is nowhere to
 * go.
 *
 * `from` is where the reviewer *is*, not where travel last sent them — a field
 * they clicked into counts. When it is not itself worth travelling to, this has
 * no way to know where it sits, so the caller resolves that against the rendered
 * order and falls back here.
 */
export function nextAttentionPath(paths: readonly string[], from: string | null): string | null {
  if (paths.length === 0) return null;
  if (from == null) return paths[0];
  const previous = paths.indexOf(from);
  if (previous === -1) return null;
  return paths[(previous + 1) % paths.length];
}
